import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'

export type ChannelId = 'investors' | 'portfolio'

// Campaign names as the founder sees them — used by the outreach hero eyebrow,
// so the page always says which of the two campaigns is on screen.
export const CHANNEL_LABELS: Record<ChannelId, string> = {
  investors: 'Investor outreach',
  portfolio: 'Warm-intro',
}

type ChannelCard = {
  id: ChannelId
  title: string
  description: string
  icon: ReactNode
}

// Fixed 2-tuple (investor, portfolio) so the split card can index [0]/[1] safely.
const CHANNEL_CARDS: [ChannelCard, ChannelCard] = [
  {
    id: 'investors',
    title: 'Investor outreach',
    description: 'Cold outreach straight to GP partners at your matched funds.',
    icon: (
      <svg width="52" height="40" viewBox="0 0 52 40" fill="none" aria-hidden="true">
        <title>Direct cold outreach to a fund</title>
        {/* you */}
        <circle cx="9" cy="20" r="5" stroke="currentColor" strokeWidth="1.4" />
        {/* cold dashed direct line */}
        <path
          d="M16 20 H34"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeDasharray="3 3"
          strokeLinecap="round"
        />
        <path
          d="M31 16.5 L34.5 20 L31 23.5"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* fund building */}
        <rect
          x="38"
          y="10"
          width="12"
          height="20"
          rx="1.5"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <path
          d="M41 15 H47 M41 20 H47 M41 25 H47"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: 'portfolio',
    title: 'Warm-intro',
    description: 'Request for warm intros via founders the fund already backs.',
    icon: (
      <svg width="52" height="40" viewBox="0 0 52 40" fill="none" aria-hidden="true">
        <title>Warm intro through a portfolio founder</title>
        {/* you */}
        <circle cx="7" cy="20" r="4.5" stroke="currentColor" strokeWidth="1.4" />
        {/* warm solid line to founder */}
        <path d="M12.5 20 H20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        {/* founder (intermediary) */}
        <circle cx="25" cy="20" r="4.5" stroke="currentColor" strokeWidth="1.4" />
        {/* warm spark above the founder */}
        <path
          d="M25 9.5 V12.5 M21.8 11 L23.4 13 M28.2 11 L26.6 13"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
        {/* line to fund */}
        <path d="M30 20 H38" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <path
          d="M35 16.5 L38.5 20 L35 23.5"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* fund building */}
        <rect
          x="42"
          y="12"
          width="9"
          height="16"
          rx="1.5"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <path
          d="M44.5 16 H48.5 M44.5 20 H48.5 M44.5 24 H48.5"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
]

// The split-card channel switcher shared by the live campaign view and the
// review modal: one orange-stroked container with an Investors / Portfolio half.
// Clicking anywhere on a half highlights it and calls onSelect (including the
// action row). Each half can render its own actions (pause/resume/edit, or the
// review on/off toggle) via renderActions; a button click still runs its own
// handler and also selects that half.
export function ChannelSwitcher({
  active,
  onSelect,
  renderActions,
}: {
  active: ChannelId
  onSelect: (id: ChannelId) => void
  /** Optional per-card action row (buttons). Click events are isolated from the card. */
  renderActions?: (id: ChannelId) => ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-primary/30 bg-primary/[0.04]">
      <div className="grid grid-cols-2">
        {CHANNEL_CARDS.map((card, i) => {
          const actions = renderActions?.(card.id)
          return (
            // biome-ignore lint/a11y/useSemanticElements: compound area with nested buttons
            <div
              key={card.id}
              role="button"
              tabIndex={0}
              aria-pressed={active === card.id}
              onClick={() => onSelect(card.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSelect(card.id)
              }}
              className={cn(
                'flex cursor-pointer flex-col transition-colors',
                i === 0 && 'border-r border-primary/20',
                active === card.id ? 'bg-primary/[0.07]' : 'hover:bg-primary/[0.03]'
              )}
            >
              <div className="flex items-center gap-4 px-5 pb-3 pt-4">
                <div
                  className={cn(
                    'shrink-0 transition-colors',
                    active === card.id ? 'text-primary' : 'text-muted-foreground'
                  )}
                >
                  {card.icon}
                </div>
                <div className="min-w-0">
                  <div
                    className={cn(
                      'text-[14px] font-semibold transition-colors',
                      active === card.id ? 'text-foreground' : 'text-foreground/70'
                    )}
                  >
                    {card.title}
                  </div>
                  <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                    {card.description}
                  </p>
                </div>
              </div>
              {actions && <div className="flex items-center gap-2 px-5 pb-4">{actions}</div>}
            </div>
          )
        })}
      </div>
    </div>
  )
}
